import {
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    Renderer2,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { AppDetails } from '../../classes/application-details';
import { AppService } from '../../services/app.service';
import { UserInfo, UserService } from '../../services/user.service';
import { WaitModalComponent } from '../modals/wait-modal/wait-modal.component';
import { SelectedScholarshipsModalComponent } from '../modals/selected-scholarships-modal/selected-scholarships-modal.component';
import { ScholarshipDescription } from '../../classes/scholarship-description';
import { ApplicationSummaryComponent } from '../application-summary/application-summary.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-summary-wrapper',
    templateUrl: './summary-wrapper.component.html',
    styleUrls: ['./summary-wrapper.component.scss'],
    imports: [
        ApplicationSummaryComponent,
        MatDividerModule,
        MatButtonModule
    ]
})
export class SummaryWrapperComponent implements OnInit, OnDestroy {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly route = inject(ActivatedRoute);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly renderer = inject(Renderer2);
    user: UserInfo;
    details: AppDetails;
    scholarshipDescriptions: ScholarshipDescription[];
    isStudent: boolean;

    ngOnInit() {
        this.renderer.removeClass(document.body, 'body');
        this.user = this.userSvc.User;
        this.route.params
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((params) => {
                let appID = +params['id'];
                this.getApplicationDetails(appID);
                this.getSelectedScholarshipDescriptions(appID);
                this.isStudent = this.user.RoleBooleans.IsStudent;
            });
    }

    viewSelected() {
        this.matDialog.open<SelectedScholarshipsModalComponent>(SelectedScholarshipsModalComponent, {
            disableClose: true,
            data: { scholarshipDescriptions: this.scholarshipDescriptions }
        });
    }

    getApplicationDetails(appID: number) {
        this.appSvc
            .GetApplicationDetails(this.user.Training, appID)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.details = res;
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    getSelectedScholarshipDescriptions(appID: number) {
        this.appSvc.GetSelectedScholarshipDescriptions(appID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (res) => {
                this.scholarshipDescriptions = res;
            },
            error: (error) => {
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    pdf() {
        let wait = this.matDialog.open(WaitModalComponent);

        this.appSvc
            .ApplicationDetailsReport(this.user.Training, this.details.AppID)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    wait.close();
                    this.saveBlobToFile(
                        res.body,
                        this.details.FirstName +
                        '_' +
                        this.details.LastName +
                        '_Application_' +
                        DateTime.now().toFormat('MMM_d_y')
                    );
                },
                error: (error) => {
                    wait.close();
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    saveBlobToFile(blob: Blob, fileName: string): void {
        //setup temporary in-memory URL and html tag
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);

        //programattically click on tag for user
        anchor.click();

        //delete tag and URL
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    }

    ngOnDestroy(): void {
        this.renderer.addClass(document.body, 'body');
    }
}
